import { Button, token } from "@may-i/mash-design-system";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface ClearAllKeysDialogProps {
  open: boolean;
  /** 초기화 대상 Key 개수 — 본문 문구에 노출 */
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ClearAllKeysDialog({ open, count, onConfirm, onCancel }: ClearAllKeysDialogProps) {
  const { t } = useTranslation("analysis-draft");
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) {
      dialog.showModal();
    }
  }, [open]);

  if (!open) return null;

  return (
    <StyledDialog
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <Header>{t("clearKeys.title")}</Header>
      <Body>{t("clearKeys.message", { count })}</Body>
      <Footer>
        <Button variant="outlined" color="inherit" size="medium" onClick={onCancel}>
          {t("clearKeys.cancel")}
        </Button>
        <Button variant="contained" color="error" size="medium" onClick={onConfirm}>
          {t("clearKeys.confirm")}
        </Button>
      </Footer>
    </StyledDialog>
  );
}

const StyledDialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: 480px;
  height: fit-content;
  margin: auto;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);

  &::backdrop {
    background: rgba(17, 17, 31, 0.32);
  }
`;

const Header = styled.div`
  padding: 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Body = styled.div`
  padding: 0 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  white-space: pre-line;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 24px;
`;
